import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { LeagueHistoryWrapper } from '@seedlang/hoc';
import { UserLeagueStore } from '@seedlang/stores';
import { flexCenterColumn } from '@seedlang/style_mixins';
import { isBlank } from '@seedlang/utils';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import LeagueRank from 'components/league/league_rank';
import Paginator from 'components/paginator';
import Spinner from 'components/spinner';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router';

const Wrapper = styled.div`
  ${flexCenterColumn()}
  width: 100%;
  max-width: 500px;
`;

const Table = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const TableRow = styled.div`
  display: flex;
  align-items: center;
  background-color: ${props => props.highlighted ? Theme.lightBlue : 'white'};
  border: 1px solid ${Theme.lightGray};
  border-radius: 5px;
  padding: 8px;
  height: 64px;
`;

const TableCell = styled.div`
  font-size: 14px;
`;

const LeagueNameCell = styled(TableCell)`
  flex: 1;
  display: flex;
  gap: 10px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeagueName = styled.div`
  display: flex;
  flex-direction: column;
`;

const RankCell = styled(TableCell)`
  width: 40px;
  text-align: center;
  font-weight: bold;
`;

const XpCell = styled(TableCell)`
  text-align: right;
  width: fit-content;
  white-space: nowrap;
  font-weight: bold;
  color: #000;
`;

const StatusCell = styled(TableCell)`
  width: 40px;
  text-align: center;
  .fa {
    font-size: 20px;
  }
`;

const ActiveTag = styled.span`
  background: ${Theme.green};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  height: fit-content;
  margin-left: ${props => props.marginLeft};
`;

const EmptyStateMessage = styled.div`
  ${flexCenterColumn()}
  text-align: center;
  color: ${Theme.gray};
  font-size: 14px;
  line-height: 24px;
  padding: 0 20px 10px 20px;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
`;

const PaginatorWrapper = styled.div`
  margin-top: 5px;
  width: 100%;
`;

const Date = styled.div`
  font-size: 12px;
  color: ${Theme.gray};
`;

@observer
class LeagueHistory extends React.Component {
  render() {
    if (isBlank(this.props.userLeagues) && UserLeagueStore.requestCounter === 0) {
      return <EmptyStateMessage>You have not completed any leagues yet.</EmptyStateMessage>;
    }
    if (UserLeagueStore.requestCounter > 0) {
      return <Spinner />;
    }
    return (
      <Wrapper>
        <Table>
          {
            this.props.userLeagues.map(userLeague => {
              return (
                <TableRow
                  key={userLeague.id}
                  highlighted={this.props.selectedLeague && userLeague.leagueId === this.props.selectedLeague.id}
                >
                  <RankCell>
                    <LeagueRank
                      margin='0'
                      rank={userLeague.rank}
                      promotionStatus={userLeague.promotionStatus}
                    />
                  </RankCell>
                  <LeagueNameCell>
                    <LeagueName>
                      {
                        this.props.backendView ? (
                          <>
                            <Link to={{ name: 'league.show', params: { leagueId: userLeague.leagueId, userId: userLeague.userId } }}>
                              <span style={{ fontWeight: 'bold' }}>{userLeague.leagueLevelName}</span>
                            </Link>
                          </>
                        ) : (
                          <span style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {userLeague.leagueLevelName}
                          </span>
                        )
                      }
                    </LeagueName>
                    {
                      userLeague.createdAt && userLeague.endTime &&
                      <Date>
                        {this.props.backendView && `${userLeague.createdAt.formattedWithWeekday} - ${userLeague.endTime.formattedDateWithFullYear}`}
                        {!this.props.backendView && userLeague.endTime.formattedDateWithFullYear}
                        {
                          userLeague.active &&
                            <ActiveTag marginLeft='20px'>Active</ActiveTag>
                        }
                      </Date>
                    }
                  </LeagueNameCell>
                  <XpCell>{userLeague.xp} XP</XpCell>
                </TableRow>
              );
            })
          }
        </Table>
        {
          this.props.userLeagues.length === 0 &&
            <EmptyStateMessage>You have not completed any leagues yet.</EmptyStateMessage>
        }
        {
          UserLeagueStore.totalCount > this.props.limit &&
            <PaginatorWrapper>
              <Paginator
                store={UserLeagueStore}
                filters={{ user_id: this.props.userId }}
                limit={this.props.limit}
              />
            </PaginatorWrapper>
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(LeagueHistoryWrapper(LeagueHistory));
